import { useState } from "react";
import { ArrowUpRightIcon, InfoIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";

/* ─── Section block ─── */

const SectionBlock = ({
  children,
  label,
  scrollable = false,
}: {
  children: React.ReactNode;
  label: string;
  scrollable?: boolean;
}) => (
  <Column className="gap-1.5">
    <Text variant="small">{label}</Text>
    <Column
      className={cn(
        "overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700",
        scrollable && "max-h-[25vh]",
      )}
    >
      {children}
    </Column>
  </Column>
);

/* ─── TLDR ─── */

const HumanInputTldrContent = () => (
  <Column className="gap-2">
    <p>
      Human Input is your advice as a recruiter to the AI model on how a
      candidate should be evaluated. If there is specific information not in the
      job description that should be used to evaluate a candidate, include it
      here. For example:
    </p>
    <ul className="list-disc space-y-1 pl-5">
      <li>
        &quot;Young, ambitious, eager-to-learn candidates do not fit in
        here&quot;
      </li>
      <li>
        &quot;Candidate must have metal machining experience or an interview
        will not even be considered&quot;
      </li>
    </ul>
    <p>
      Details like these that arise during the intake or sales process are
      exactly what should go into this input. The AI treats Human Input as the
      highest-priority source of truth — if it contradicts the Job Overview, the
      AI will defer to what you wrote here.
    </p>
  </Column>
);

const AprRubricTldrContent = () => (
  <Column className="gap-2">
    <p>
      The APR Rubric is used to rate candidacies as they arrive in a job&apos;s
      pipeline. The goal is to configure it as closely as possible to what an
      actual staffer would look for. It is auto-generated in the format of the
      Example Rubric below and is heavily influenced by any additional context
      provided via Human Input.
    </p>
    <p>
      A few alternative formats have shown merit, but time constraints have
      limited iteration on the base example, so most APR Rubrics follow the
      structure of the provided Example Rubric. If you have the bandwidth, we
      encourage you to experiment with different variations:
    </p>
    <ul className="list-disc space-y-1 pl-5">
      <li>
        <Row className="items-center gap-1">
          <span className="font-medium">Concise</span>
          <span>—</span>
          <a
            className="inline-flex items-center gap-0.5 text-primary-700 hover:underline"
            href="https://internal-recruiter.laborup.com/companies/c5d8c13d-0f5f-428a-90c5-6be67cb10498/jobs/b4de6c8a-ca21-4901-ad53-3500469de4ba?t=internal-config"
            rel="noopener noreferrer"
            target="_blank"
          >
            Timken: Production Tech
            <ArrowUpRightIcon className="h-3 w-3" />
          </a>
        </Row>
      </li>
      <li>
        <Row className="items-center gap-1">
          <span className="font-medium">Point System</span>
          <span>—</span>
          <a
            className="inline-flex items-center gap-0.5 text-primary-700 hover:underline"
            href="https://internal-recruiter.laborup.com/companies/1d195591-409f-419b-8086-a19821a24294/jobs/483a9733-ef47-498d-aa07-2ee73fa43cb7?t=internal-config"
            rel="noopener noreferrer"
            target="_blank"
          >
            Bush: Production Operator
            <ArrowUpRightIcon className="h-3 w-3" />
          </a>
        </Row>
      </li>
    </ul>
  </Column>
);

const tldrContentByVariant = {
  "human-input": HumanInputTldrContent,
  "apr-rubric": AprRubricTldrContent,
};

/* ─── AI Prompt ─── */

const AiPrompt = () => (
  <SectionBlock label="AI Prompt" scrollable>
    <Column className="gap-3">
      <p>
        You are an expert recruiter and talent evaluator with deep experience
        hiring in industrial, skilled trades, manufacturing, and technical
        roles. Your job is to create a resume-first star-rating rubric that
        helps human screeners quickly understand what &quot;strong signal&quot;
        looks like for this role before deciding whether a candidate is worth a
        human phone call.
      </p>
      <p>
        This rubric is not a definitive hiring decision. It is a signal tool.
        The goal is to predict the likelihood that someone is a great fit, based
        on what tends to show up in resumes and (when available) basic
        screening-question answers. Prioritize clarity, tradeoffs, and practical
        recruiting signal over perfection.
      </p>
      <p>
        Use the contents attached to infer what the job entails, what
        &quot;good&quot; looks like, what industries matter most, and what
        signals correlate with real performance in this job family. Here is an
        example of a rubric that has worked very well:
      </p>

      <p className="rounded border border-slate-300 bg-slate-100 px-3 py-2 text-center font-bold text-slate-600">
        THE EXAMPLE RUBRIC (shown below) IS INJECTED HERE
      </p>

      <p className="font-semibold">How to use each input</p>
      <p>
        <strong>HUMAN_INPUT_FIELD</strong> is the highest priority input and
        must heavily shape the rubric if it&apos;s included. It contains
        specific intelligence from a recruiter who spoke directly with the
        client — things that never appear in a job description: red flags from
        past bad hires, niche background preferences, &quot;do not want&quot;
        profiles, or tenure/personality patterns the client cares about (e.g.
        &quot;hopping is a dealbreaker,&quot; &quot;candidates from X company
        don&apos;t translate well here,&quot; &quot;we need someone seasoned,
        not the eager fresh-out-of-school type&quot;). Treat every detail as a
        high-confidence screening criterion. Weave them directly into positive
        signals, negative signals, and personas across all star tiers. If it
        conflicts with the job description, trust the human input.
      </p>
      <p>
        <strong>JOB_DESCRIPTION</strong> is mainly used to understand what the
        job entails and what tasks exist, not to strictly enforce arbitrary
        requirements. Do not overweight blanket requirements like &quot;X years
        required,&quot; because those are often random or inflated.
      </p>
      <p>
        <strong>EXAMPLE_RUBRIC_FORMAT</strong> must be followed precisely. Match
        its structure, ordering, tone, and level of detail. Use it as the
        template for the output formatting and section layout.
      </p>

      <p className="font-semibold">Core principles for building the rubric</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          This is resume-first. You are evaluating signals present in a resume
          and optionally in screening answers.
        </li>
        <li>
          You are modeling recruiter judgment under uncertainty. It is okay to
          make reasonable inferences when resumes lack detail, because the
          rubric is meant to surface signal, not prove truth.
        </li>
        <li>
          Tenure matters. All else equal, a candidate with relevant skills plus
          stable tenure is stronger signal than the same skills with unstable
          tenure. Tenure alone is not sufficient, but it is a meaningful
          positive signal.
        </li>
        <li>
          Industry relevance matters. Doing the &quot;same job title&quot; in a
          highly relevant industry can be more predictive than a long list of
          generic skills. Strongly weight the industries and environments that
          matter for this role (as indicated by the human input).
        </li>
        <li>
          Be explicit about tradeoffs in the middle tiers (2–4 stars). 5-star
          profiles are close on everything. 1-star profiles are far off on
          everything. 2–4 stars contain nuance and require careful reasoning
          about tradeoffs between industry relevance, depth of skill, tenure,
          and clarity of evidence.
        </li>
        <li>
          Screening questions are typically surface-level (for example:
          &quot;How many years do you have of TIG?&quot;, &quot;In what
          industries have you welded?&quot;, &quot;How many years of orbital
          welding do you have?&quot;). Do not pretend screening answers include
          deep nuance. Use screening answers mainly to support or contradict
          resume signals and to add basic structure where resumes are vague.
        </li>
        <li>
          Avoid invented constraints. Don&apos;t require commute-time, exact
          travel logistics, or hyper-specific details unless the human input
          explicitly says those are required and the screening questions capture
          them.
        </li>
        <li>
          The rubric should help a human decide: &quot;Do I want to spend time
          reviewing and calling this person?&quot; It should be written to be
          usable for triage in a real recruiting pipeline.
        </li>
      </ul>

      <p className="font-semibold">What you must produce</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          A complete rubric with five rating tiers: 5 Star, 4 Star, 3 Star, 2
          Star, 1 Star.
        </li>
        <li>
          For each rating tier, include these three sections: Positive Signals
          (resume + screening answers), Negative Signals, and Different Ways
          This Could Look (personas).
        </li>
        <li>
          Personas are mandatory and extremely important. Each star rating must
          include 5 personas. Personas must be varied and represent multiple
          ways success can look, capturing tradeoffs across skill depth,
          industry relevance, tenure, and proof quality.
        </li>
        <li>
          Each persona should be about 50 words and should read like a realistic
          candidate profile a recruiter would recognize.
        </li>
      </ul>

      <p className="font-semibold">
        Content requirements (how to write each section)
      </p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <strong>Positive Signals</strong> should describe what produces strong
          signal in a resume for this role. Use concrete resume-evidence
          language: titles, industries, environments, tools, equipment, systems,
          process keywords, scope indicators, outcomes, inspection/QA terms
          (when relevant), and tenure patterns. Include how to interpret
          screening-question answers when present as a supporting or
          contradicting signal.
        </li>
        <li>
          <strong>Negative Signals</strong> should describe what produces weak
          signal, uncertainty, or risk in a resume for this role. Include red
          flags, missing must-have signals, irrelevant industry patterns,
          contradictions between screening answers and the resume, and
          instability patterns. Keep it signal-based and resume-based.
        </li>
        <li>
          <strong>Personas</strong> should be written as vivid, realistic
          archetypes. Each persona should include: likely background, typical
          employers/environments, what the resume probably looks like, what it
          signals, and why it maps to this star tier. Make personas diverse in
          how they arrive at fit while staying realistic for the role.
        </li>
      </ul>

      <p className="font-semibold">Guidance for handling incomplete profiles</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          If a resume has limited bullets, infer likely responsibilities from
          job title + employer + industry when that inference is reasonable and
          widely understood.
        </li>
        <li>
          When inference is uncertain, describe it as signal rather than fact.
          This rubric is about likelihood, not proof.
        </li>
        <li>
          If screening answers are missing, the rubric should still function
          based on resume signals alone.
        </li>
      </ul>

      <p className="font-semibold">Style Guidelines</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Output must use only &lt;ul&gt;, &lt;li&gt;, &lt;p&gt;, and
          &lt;strong&gt;.
        </li>
      </ul>

      <p className="font-semibold">Output instructions</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Output only the rubric, in the exact format implied by the Example
          Rubric.
        </li>
        <li>Do not include any preamble, explanation, or meta commentary.</li>
        <li>
          Do not reference these instructions or the variable names in the
          output.
        </li>
      </ul>

      <p>
        Given the example, the job context, and the human input, generate a
        rubric for the job.
      </p>
    </Column>
  </SectionBlock>
);

/* ─── Example Rubric ─── */

const ExampleRubric = () => (
  <SectionBlock label="Example Rubric Provided to ChatGPT" scrollable>
    <Column className="gap-3">
      <p>
        <strong>5 Star — &quot;Call immediately&quot;</strong>
      </p>
      <p>Positive Signals (resume + screening answers)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          8+ years documented TIG + stick + combo pipe welding, including
          stainless + carbon, and all-position language (e.g., 6G/all-position,
          open root).
        </li>
        <li>
          Clear code/QA environment signals: &quot;X-ray/RT,&quot;
          &quot;UT,&quot; &quot;ASME Section IX,&quot; &quot;WPS/PQR,&quot;
          &quot;coded welder,&quot; &quot;nuclear/refinery/power
          generation,&quot; &quot;turnaround/outage.&quot;
        </li>
        <li>
          Evidence of test readiness: recent 6G (or equivalent) passes listed
          with date/employer, or repeated statements like &quot;pass every site
          test,&quot; &quot;RT quality,&quot; &quot;zero repairs,&quot;
          &quot;high-pressure pipe.&quot;
        </li>
        <li>
          Tenure strength: multiple multi-year stints OR repeated project
          completions with the same contractors.
        </li>
        <li>
          Screening answers reinforce alignment and do not contradict the resume
          on core items.
        </li>
      </ul>
      <p>Negative Signals</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Very little. Possibly missing a specific paper cert, but repeated
          inspected work and testing are evident.
        </li>
        <li>
          Minor ambiguity on one item, but the overall pattern signals a
          site-test passer.
        </li>
        <li>
          Primary concern would be direct contradictions between screening
          answers and resume.
        </li>
      </ul>
      <p>Different Ways This Could Look (personas)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Nuclear outage road veteran with repeat shutdown seasons and deep QA
          vocabulary.
        </li>
        <li>
          Refinery/chemical turnaround ace with strong inspected-weld language
          and tenure.
        </li>
        <li>
          Power-plant welder focused on high-pressure piping and outage cycles.
        </li>
        <li>
          Shop-to-field hybrid with documented code pipe and recent testing.
        </li>
        <li>Specialist combo welder with concise but high-signal proof.</li>
      </ul>

      <p>
        <strong>4 Star — &quot;Strong call candidate, minor gaps&quot;</strong>
      </p>
      <p>Positive Signals (resume + screening answers)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>5–10 years TIG/stick experience with pipe or combo exposure.</li>
        <li>Some inspected or industrial environment markers.</li>
        <li>Good tenure, even if not nuclear-specific.</li>
        <li>Screening answers generally support the resume narrative.</li>
      </ul>
      <p>Negative Signals</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          Missing one key proof point (explicit 6G, recent test date, or
          stainless depth).
        </li>
        <li>Industry alignment is close but not perfect.</li>
        <li>
          Possible inflation or mismatch between resume and screening answers.
        </li>
      </ul>
      <p>Different Ways This Could Look (personas)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Shutdown welder without nuclear branding.</li>
        <li>Refinery welder stronger on carbon than stainless.</li>
        <li>Pipefitter-welder combo with ambiguous ownership.</li>
        <li>Code shop welder transitioning to field work.</li>
        <li>Strong welder missing one documented checkbox.</li>
      </ul>

      <p>
        <strong>3 Star — &quot;Possible, but needs validation&quot;</strong>
      </p>
      <p>Positive Signals (resume + screening answers)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>3–7 years welding with some pipe, TIG, or stick exposure.</li>
        <li>Adjacent industrial or maintenance environments.</li>
        <li>Some tenure or stability present.</li>
        <li>Screening answers indicate plausible but unproven fit.</li>
      </ul>
      <p>Negative Signals</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Limited proof of inspected or code pipe work.</li>
        <li>Pipe or stainless TIG experience is sporadic or unclear.</li>
        <li>Industry relevance is mixed.</li>
      </ul>
      <p>Different Ways This Could Look (personas)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Shop welder with occasional pipe exposure.</li>
        <li>Industrial maintenance welder with unclear code level.</li>
        <li>Carbon-heavy welder with uncertain stainless depth.</li>
        <li>Helper transitioning into welding ownership.</li>
        <li>Strong welder in a different industry context.</li>
      </ul>

      <p>
        <strong>
          2 Star — &quot;Low signal, call only if pipeline needs volume&quot;
        </strong>
      </p>
      <p>Positive Signals (resume + screening answers)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          2–5 years of welding experience with at least one relevant process.
        </li>
        <li>Some stability or tenure.</li>
        <li>
          Screening answers are honest and aligned with limited resume scope.
        </li>
      </ul>
      <p>Negative Signals</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>No clear pipe or combo indicators.</li>
        <li>No inspected or code environment signals.</li>
        <li>Vague resume and low technical specificity.</li>
      </ul>
      <p>Different Ways This Could Look (personas)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>MIG-heavy production welder aiming for pipe.</li>
        <li>Light fabrication welder with limited positions.</li>
        <li>Early-career welder with scattered experience.</li>
        <li>TIG welder in non-code settings.</li>
        <li>Helper with ambition but no weld ownership.</li>
      </ul>

      <p>
        <strong>1 Star — &quot;Do not prioritize&quot;</strong>
      </p>
      <p>Positive Signals (resume + screening answers)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Minimal exposure or interest in welding.</li>
        <li>Any tenure is noted but insufficient.</li>
      </ul>
      <p>Negative Signals</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>No meaningful TIG, stick, or pipe experience.</li>
        <li>No code, inspection, or QA environment exposure.</li>
        <li>Resume and screening answers are inconsistent or unrealistic.</li>
      </ul>
      <p>Different Ways This Could Look (personas)</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Non-welder or unrelated trade background.</li>
        <li>MIG-only production welder with no expansion signal.</li>
        <li>Unclear resume with weak screening answers.</li>
        <li>High risk due to availability or constraints.</li>
        <li>Entry-level assistant far from readiness.</li>
      </ul>
    </Column>
  </SectionBlock>
);

/* ─── Root component ─── */

export const GenerateRubricInfoGuide = ({
  className,
  variant = "human-input",
}: {
  className?: string;
  variant?: "human-input" | "apr-rubric";
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const TldrContent = tldrContentByVariant[variant];

  return (
    <>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <InfoIcon
              className={cn(
                "h-3.5 w-3.5 shrink-0 cursor-pointer text-slate-400",
                className,
              )}
              onClick={(e) => {
                e.preventDefault();
                setModalOpen(true);
              }}
            />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-sm p-3">
            <Column className="gap-2">
              <TldrContent />
              <button
                className="cursor-pointer self-start text-xs font-medium text-primary-700 hover:underline"
                onClick={() => {
                  setModalOpen(true);
                }}
                type="button"
              >
                View full AI prompt &amp; Example Rubric
              </button>
            </Column>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-h-[85vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>How Rubric Generation Works</DialogTitle>
          </DialogHeader>
          <Column className="gap-3">
            <SectionBlock label="TLDR">
              <TldrContent />
            </SectionBlock>
            <AiPrompt />
            <ExampleRubric />
          </Column>
        </DialogContent>
      </Dialog>
    </>
  );
};
